import { objectHasKeys } from '@art-suite/art-core-ts';
import { isOptionScoped, isOtherTask, ScanOptionOptimized, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';
import { useMemo } from 'react';

import { useScanEditingContextSlice } from '@/Client/State';
import { css } from '@emotion/react';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';

import { LabeledInput } from '../../Widgets';
import { InlineOptionEditor } from './InlineOptionEditor';
import { ScanValueView } from './ScanValueView';

const INLINE_TASK_DETAILS_EDITOR_STYLE = css`
  label: INLINE_TASK_DETAILS_EDITOR_STYLE;
  display: flex;
  flex-direction: column;
  background: var(--background);
`;

const OPTIONS_TABLE_STYLE = css`
  label: OPTIONS_TABLE_STYLE;
  display: flex;
  flex-direction: column;
  background: var(--background);
  gap: ${LAYOUT2.spacing1}px;
`;

const OPTIONS_TABLE_CONTAINER_STYLE = css`
  label: OPTIONS_TABLE_CONTAINER_STYLE;
  padding-right: ${LAYOUT2.spacing1}px;
  padding-bottom: ${LAYOUT2.spacing1}px;
`;

const TASK_DESCRIPTION_STYLE = css`
  label: TASK_DESCRIPTION_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text2);
  padding-top: ${LAYOUT2.spacing}px;
  padding-bottom: ${LAYOUT2.spacing2}px;
`;

const VALUES_TABLE_STYLE = css`
  label: VALUES_TABLE_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  background: var(--background);
  padding-right: ${LAYOUT2.spacing * 20.5}px;
  padding-bottom: ${LAYOUT2.spacing}px;
`;

export const InlineTaskEditorDetails = ({
  task,
  taskExpanded,
  groupId,
  toggleTaskExpanded,
}: {
  task: ScanTaskOptimized;
  taskExpanded: boolean;
  groupId: string;
  toggleTaskExpanded: () => void;
}) => {
  const { scanIsEditable, scanDataMutators } = useScanEditingContextSlice(['scanIsEditable', 'scanDataMutators']);
  const optionsByTemplateId: Record<string, ScanOptionOptimized[]> = useMemo(() => {
    const optionsByTemplateId: Record<string, ScanOptionOptimized[]> = {};
    for (const option of task.options) {
      (optionsByTemplateId[option.templateId] ??= []).push(option);
    }
    return optionsByTemplateId;
  }, [task.options]);

  const { optionTemplates = [] } = task.template;

  return (
    <div css={INLINE_TASK_DETAILS_EDITOR_STYLE}>
      {task.template.description && taskExpanded && (
        <div css={TASK_DESCRIPTION_STYLE} onClick={toggleTaskExpanded}>
          {task.template.description}
        </div>
      )}
      {(objectHasKeys(task.template.values) || isOtherTask(task)) && (
        <div css={VALUES_TABLE_STYLE}>
          {isOtherTask(task)
            && (
              <LabeledInput
                label='non-standard task label'
                value={task.customLabel ?? ''}
                disabled={!scanIsEditable}
                onUpdate={value => {
                  if (!scanIsEditable) return;
                  scanDataMutators.updateTask(groupId, task.id, { customLabel: value });
                }}
              />
            )}
          {Object.entries(task.template.values || {}).map(([key, value]) => (
            <ScanValueView
              key={key}
              size='medium'
              valueKey={key}
              value={task.values[key]}
              templateValue={task.template.values![key]}
              updateValue={(key, newValue) => {
                if (!scanIsEditable) return;
                scanDataMutators.updateTask(groupId, task.id, { values: { [key]: newValue } });
              }}
            />
          ))}
        </div>
      )}
      {optionTemplates.length > 0 && (
        <div css={OPTIONS_TABLE_CONTAINER_STYLE}>
          <div css={OPTIONS_TABLE_STYLE} onClick={taskExpanded ? undefined : toggleTaskExpanded}>
            {optionTemplates.map(optionTemplate =>
              (optionsByTemplateId[optionTemplate.id] ?? [undefined]).map(option => (
                (taskExpanded || isOptionScoped(option)) && (
                  <InlineOptionEditor
                    key={option?.id ?? optionTemplate.id}
                    expanded={taskExpanded}
                    groupId={groupId}
                    option={option}
                    optionTemplate={optionTemplate}
                    scanDataMutators={scanDataMutators}
                    task={task}
                  />
                )
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
